/**
 * Le date e le ore si scrivono nella lingua dell'APP, non del browser.
 *
 * Otto punti passavano `undefined` (o `[]`) come locale a `toLocaleDateString` /
 * `toLocaleTimeString`, e quel valore non vuol dire «la lingua scelta»: vuol dire
 * «quella del browser». Chi tiene Chrome in inglese e l'app in olandese leggeva
 * «Mar 3» accanto a un'interfaccia in olandese, e chi ha un browser in italiano
 * vedeva le date italiane anche dopo essere passato al portoghese. Non è un
 * dettaglio da poco: nel formato ci sta anche l'ORDINE (giorno/mese o mese/giorno)
 * e il ciclo a 12 o 24 ore, che cambiano il SIGNIFICATO di quello che si legge.
 *
 * La lingua si legge da `<html lang>`, che `i18n/index.ts` tiene allineato alla
 * lingua scelta (all'avvio e a ogni `languageChanged`). Non si importa l'istanza
 * i18n, e non per pigrizia: questo modulo lo usano componenti minuscoli come
 * `ArenaCard`, e importare `../i18n` da lì tirerebbe dentro l'inizializzazione
 * completa di i18next a ogni test che monta uno di quei componenti — cosa che
 * infatti ha rotto subito una decina di file di test che finge `react-i18next`.
 * L'attributo del documento è la stessa informazione, senza il fardello.
 *
 * E non si passa come parametro: chi disegna una data ha già `t` da
 * `useTranslation`, quindi si ridisegna da sé al cambio di lingua — e queste
 * funzioni, chiamate durante quel ridisegno, leggono già il valore nuovo. Un
 * parametro in più su otto chiamate sarebbe stato solo un'altra cosa da
 * dimenticare.
 *
 * Il ripiego `'en'` copre il caso in cui l'attributo non ci sia (una pagina
 * pre-generata, un test che monta un componente da solo): meglio una data inglese
 * che un'eccezione.
 */

/* Solo il TIPO, che si cancella in compilazione: nessun i18next a runtime
   (vedi sopra perche' questo modulo non lo importa). */
import type { TFunction } from 'i18next';

function locale(): string {
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.lang || 'en';
}

/** «3 mar» — giorno e mese, per gli elenchi. */
export function dataBreve(iso: string | number | Date): string {
  return new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'short' });
}

/** L'ora e i minuti, nel ciclo (12 o 24 ore) della lingua scelta. */
export function oraBreve(data: Date | string | number): string {
  return new Date(data).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

/**
 * Quando inizia una cosa che inizia fra piu' di un'ora: l'ora, e il GIORNO
 * quando non e' oggi.
 *
 * `oraBreve` da sola dice «18:58», e su una card quello si legge come OGGI —
 * anche quando il torneo e' domani, o fra una settimana. E' il modo piu' rapido
 * di far sembrare sbagliati degli orari giusti: chi aveva creato un torneo per
 * l'indomani alle 19:00 leggeva «Inizia alle 18:58» la sera prima, cioe' un
 * orario gia' passato.
 *
 * Il confronto e' fra GIORNI DI CALENDARIO locali, non fra scarti di 24 ore:
 * alle 23:00, un torneo alle 08:00 dista nove ore ma e' «domani», ed e' quello
 * che una persona vuole leggere. `Math.round` sulla differenza fra le due
 * mezzanotti regge anche la notte del cambio d'ora, dove un giorno dura 23 o 25
 * ore.
 *
 * Ritorna la CHIAVE e i valori invece della frase fatta: la traduzione la fa
 * chi ha `t`, e le tre forme restano in `tournament.card.*` insieme alle altre.
 */
export function orarioDiAvvio(data: Date | string | number): {
  chiave: string;
  valori: { time: string; date?: string };
} {
  const d = new Date(data);
  const mezzanotte = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const giorni = Math.round((mezzanotte(d) - mezzanotte(new Date())) / 86_400_000);
  /* Una data non valida rende `giorni` NaN, e NaN e' falso in tutti i confronti:
     senza questa riga si cadrebbe sul ramo piu' loquace, «il Invalid Date alle
     Invalid Date», invece che sul piu' silenzioso. Nessun chiamante di oggi ci
     arriva, ma la funzione non deve peggiorare le cose da sola. */
  if (giorni <= 0 || Number.isNaN(giorni)) return { chiave: 'tournament.card.startsAt', valori: { time: oraBreve(d) } };
  if (giorni === 1) return { chiave: 'tournament.card.startsAtTomorrow', valori: { time: oraBreve(d) } };
  return { chiave: 'tournament.card.startsAtDate', valori: { date: dataBreve(d), time: oraBreve(d) } };
}

/** Una data con le opzioni che serve a chi chiama, ma sempre nella lingua giusta. */
export function dataConOpzioni(
  iso: string | number | Date,
  opzioni: Intl.DateTimeFormatOptions,
): string {
  return new Date(iso).toLocaleDateString(locale(), opzioni);
}

/**
 * Un numero decimale con il separatore della LINGUA scelta.
 *
 * `toFixed(1)` produce sempre il punto: «7.5». In italiano, tedesco, francese,
 * spagnolo, polacco e portoghese il separatore decimale è la VIRGOLA, e un punto
 * in mezzo a un numero lì si legge come separatore delle migliaia — cioè
 * settemilacinquecento invece di sette e mezzo. Sui punteggi di spareggio di uno
 * Swiss (Buchholz, Sonneborn-Berger) è la differenza fra una classifica che si
 * capisce e una che sembra impazzita.
 */
export function decimale(valore: number, cifre = 1): string {
  return valore.toLocaleString(locale(), {
    minimumFractionDigits: cifre,
    maximumFractionDigits: cifre,
  });
}

/**
 * Quando comincia una gara, nella forma che cambia con la distanza: l'ORARIO
 * finche' manca piu' di un'ora («Inizia alle 21:00», «domani», la data), il
 * conto alla rovescia in MINUTI nell'ultima ora, al SECONDO negli ultimi cinque
 * minuti, e «inizia ora» quando l'ora e' passata.
 *
 * Una sola cascata per i quattro posti che la mostrano — card di tornei e arene,
 * i due dettagli, la card «Prossimi» della home — che prima ne avevano tre copie
 * e due regole diverse (il dettaglio del torneo diceva sempre l'orario, quello
 * dell'arena contava sempre, anche a giorni di distanza).
 *
 * `adesso` e' un parametro per i test e per chi ha gia' un orologio suo.
 */
export type AvvioAdattivo =
  | { tipo: 'orario'; chiave: string; valori: { time: string; date?: string } }
  | { tipo: 'minuti'; minuti: number }
  | { tipo: 'secondi'; minuti: number; secondi: number }
  | { tipo: 'ora' };

export function avvioAdattivo(startsAt: Date | string | number, adesso: number = Date.now()): AvvioAdattivo {
  const diff = new Date(startsAt).getTime() - adesso;
  /* Una data illeggibile non diventa un conto alla rovescia inventato: ripiega
     sulla frase dell'orario, che per una data non valida e' gia' la piu' sobria. */
  if (Number.isNaN(diff)) return { tipo: 'orario', ...orarioDiAvvio(startsAt) };
  if (diff <= 0) return { tipo: 'ora' };
  const minuti = Math.floor(diff / 60_000);
  if (minuti < 5) return { tipo: 'secondi', minuti, secondi: Math.floor((diff % 60_000) / 1000) };
  if (minuti <= 60) return { tipo: 'minuti', minuti };
  return { tipo: 'orario', ...orarioDiAvvio(startsAt) };
}

/**
 * La frase di `avvioAdattivo`, con le chiavi di sempre (`tournament.card.*`), e
 * se deve lampeggiare: negli ultimi cinque minuti e quando e' l'ora.
 */
export function testoDiAvvio(t: TFunction, a: AvvioAdattivo): { text: string; pulsing: boolean } {
  switch (a.tipo) {
    case 'ora':
      return { text: t('tournament.card.startingNow'), pulsing: true };
    case 'secondi':
      return {
        text: t('tournament.card.startsInCountdown', { time: `${a.minuti}:${String(a.secondi).padStart(2, '0')}` }),
        pulsing: true,
      };
    case 'minuti':
      return { text: t('tournament.card.startsInCountdown', { time: `${a.minuti}m` }), pulsing: false };
    case 'orario':
      return { text: t(a.chiave, a.valori), pulsing: false };
  }
}
